import { useState, useContext, useEffect, useMemo } from "react";
import { toast } from "react-toastify";
import {
  Card,
  Box,
  Typography,
  InputAdornment,
  IconButton,
  LinearProgress,
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";

import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { updatePassword, requestOwnPasswordReset } from "../../../../api/userApi";
import { LanguageContext } from "../../../../context/LanguageContext";
import { COLORS } from "theme/tokens";

// 與其他頁面一致的灰底 filled input
const FILLED_INPUT_SX = {
  "& .MuiOutlinedInput-root": {
    backgroundColor: COLORS.grayLightest,
    borderRadius: 1.5,
    "& fieldset": { border: "none" },
    "&:hover fieldset": { border: "none" },
    "&.Mui-focused": {
      backgroundColor: COLORS.white,
      "& fieldset": { border: "1px solid", borderColor: "info.main" },
    },
  },
  "& .MuiOutlinedInput-input": {
    padding: "12px 14px",
    fontSize: 14,
  },
};

export default function ChangePasswordPage() {
  const { componentLanguageData, loadLanguageFile, language } = useContext(LanguageContext);

  useEffect(() => {
    loadLanguageFile("ChangePasswordPage", "profile/ChangePasswordPage/ChangePasswordPage_lang");
  }, [language]);

  const LANGUAGE = useMemo(
    () => componentLanguageData.ChangePasswordPage || { Main: {}, Alert: {}, Error: {} },
    [componentLanguageData]
  );
  const L = LANGUAGE.Main || {};

  const [form, setForm] = useState({
    oldPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  // 每個欄位獨立的顯隱狀態（與設計圖一致：點 X 欄的眼睛只切換 X 欄）
  const [showOld, setShowOld] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const passwordValid =
    /^(?=.*[A-Z])(?=.*\d)[A-Za-z\d!@#$%^&*()_\-+=\[\]{};:'",.<>/?\\|`~]{8,}$/.test(form.newPassword);

  // 密碼強度（0~5 分制，對應 LinearProgress 百分比）
  const getPasswordStrength = (pwd) => {
    let score = 0;
    if (pwd.length >= 8) score++;
    if (/[A-Z]/.test(pwd)) score++;
    if (/[0-9]/.test(pwd)) score++;
    if (/[^A-Za-z0-9]/.test(pwd)) score++;
    if (pwd.length >= 12) score++;

    if (score <= 1) return { label: L.Content7 || "弱", color: "error", percent: 20 };
    if (score === 2) return { label: L.Content8 || "中", color: "warning", percent: 50 };
    if (score === 3 || score === 4) return { label: L.Content9 || "強", color: "warning", percent: 75 };
    return { label: L.Content10 || "非常強", color: "success", percent: 100 };
  };
  const strength = getPasswordStrength(form.newPassword);

  // 給帶眼睛切換 + 強度條的 input 用的通用 endAdornment generator
  const eyeAdornment = (show, setShow) => (
    <InputAdornment position="end">
      <IconButton onClick={() => setShow((v) => !v)} edge="end" size="small" tabIndex={-1}>
        {show ? <Visibility fontSize="small" /> : <VisibilityOff fontSize="small" />}
      </IconButton>
    </InputAdornment>
  );

  // 🔑 忘記舊密碼：寄重設信到自己的註冊信箱（Google 登入者找回 EXE 密碼的唯一自助管道）
  //    刻意不在此直接改密碼 —— 一律經 email 驗證，避免他人拿到已登入的電腦就能改密碼奪帳號。
  const [sendingReset, setSendingReset] = useState(false);
  const handleSendReset = async () => {
    if (sendingReset) return;
    setSendingReset(true);
    try {
      const r = await requestOwnPasswordReset();
      const tpl = LANGUAGE.Alert?.Content3 || "重設連結已寄至 {email}，請於 30 分鐘內完成。";
      toast.success(tpl.replace("{email}", r?.maskedEmail || ""));
    } catch (err) {
      toast.error(LANGUAGE.Error?.Content5 || "發送失敗");
    } finally {
      setSendingReset(false);
    }
  };

  const handleSubmit = async () => {
    if (form.newPassword !== form.confirmPassword) {
      toast.error(LANGUAGE.Error?.Content1 || "新密碼與確認密碼不一致");
      return;
    }
    if (!passwordValid) {
      toast.error(LANGUAGE.Error?.Content2 || "密碼強度不足");
      return;
    }

    try {
      const result = await updatePassword({
        oldPassword: form.oldPassword,
        newPassword: form.newPassword,
      });
      if (result?.success) {
        toast.success(LANGUAGE.Alert?.Content1 || "密碼更新成功！請重新登入");
        setForm({ oldPassword: "", newPassword: "", confirmPassword: "" });
        localStorage.removeItem("access_token");
        setTimeout(() => {
          window.location.href = "/sign-in";
        }, 2000);
      } else {
        toast.error(LANGUAGE.Alert?.Content2 || "密碼更新回傳錯誤");
      }
    } catch (err) {
      const code = err.response?.data?.message;
      let msg = LANGUAGE.Error?.Content5 || "修改失敗";
      if (code === "UserNotFound") msg = LANGUAGE.Error?.Content3 || "用戶不存在";
      else if (code === "IncorrectOldPassword") msg = LANGUAGE.Error?.Content4 || "舊密碼錯誤";
      toast.error(msg);
    }
  };

  // 必填星號樣式
  const RequiredStar = () => <span style={{ color: COLORS.error }}>*</span>;

  // Field label row
  const FieldLabel = ({ children }) => (
    <Typography
      variant="caption"
      fontWeight="medium"
      sx={{ display: "block", mb: 0.75, color: "text.primary", fontSize: "0.85rem" }}
    >
      {children}
      <RequiredStar />
    </Typography>
  );

  return (
    <MDBox pt={2} pb={1}>
      {/* 單欄靠左卡片（對齊截圖二：較窄、靠左） */}
      <Box display="flex" justifyContent="flex-start">
        <Card sx={{ p: 3, borderRadius: 3, boxShadow: 1, width: "100%", maxWidth: 520 }}>
          {/* 標題列 */}
          <Box display="flex" alignItems="center" gap={1} mb={2.5}>
            <Box sx={{ width: 4, height: 18, bgcolor: COLORS.main, borderRadius: 1 }} />
            <MDTypography variant="subtitle1" fontWeight="bold">
              {L.CardTitle || "更新您的登入與交易密碼"}
            </MDTypography>
          </Box>

          {/* 舊密碼 */}
          <Box mb={2}>
            <FieldLabel>{L.FieldOldPassword || "舊密碼"}</FieldLabel>
            <MDInput
              type={showOld ? "text" : "password"}
              name="oldPassword"
              placeholder={L.PlaceholderOldPassword || "輸入您的舊密碼"}
              fullWidth
              value={form.oldPassword}
              onChange={handleChange}
              sx={FILLED_INPUT_SX}
              InputProps={
                form.oldPassword.length > 0
                  ? { endAdornment: eyeAdornment(showOld, setShowOld) }
                  : undefined
              }
            />
          </Box>

          {/* 新密碼 + 強度條 */}
          <Box mb={2}>
            <FieldLabel>{L.FieldNewPassword || "新密碼"}</FieldLabel>
            <MDInput
              type={showNew ? "text" : "password"}
              name="newPassword"
              placeholder={L.PlaceholderNewPassword || "需包含大寫字母、數字且至少 8 碼"}
              fullWidth
              value={form.newPassword}
              onChange={handleChange}
              sx={FILLED_INPUT_SX}
              InputProps={
                form.newPassword.length > 0
                  ? { endAdornment: eyeAdornment(showNew, setShowNew) }
                  : undefined
              }
            />
            {form.newPassword.length > 0 && (
              <Box mt={1}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                  <Typography variant="caption" sx={{ color: COLORS.black }}>
                    {L.StrengthLabel || "強度"}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: COLORS.main, fontWeight: 600 }}
                  >
                    {strength.label}
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={strength.percent}
                  sx={{
                    height: 4,
                    borderRadius: 2,
                    backgroundColor: COLORS.lighter,
                    "& .MuiLinearProgress-bar": { backgroundColor: COLORS.main },
                  }}
                />
              </Box>
            )}
          </Box>

          {/* 確認密碼 */}
          <Box mb={3}>
            <FieldLabel>{L.FieldConfirmPassword || "確認密碼"}</FieldLabel>
            <MDInput
              type={showConfirm ? "text" : "password"}
              name="confirmPassword"
              placeholder={L.PlaceholderConfirmPassword || "再次輸入您的新密碼"}
              fullWidth
              value={form.confirmPassword}
              onChange={handleChange}
              sx={FILLED_INPUT_SX}
              InputProps={
                form.confirmPassword.length > 0
                  ? { endAdornment: eyeAdornment(showConfirm, setShowConfirm) }
                  : undefined
              }
            />
          </Box>

          {/* 更新密碼按鈕 — 右下角 */}
          <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
            <Box>
              <Typography variant="caption" sx={{ color: COLORS.grayDark, mr: 1 }}>
                {L.Content11 || "忘記舊密碼？"}
              </Typography>
              <MDButton
                variant="text"
                onClick={handleSendReset}
                disabled={sendingReset}
                sx={{
                  textTransform: "none",
                  fontWeight: 700,
                  color: COLORS.main,
                  px: 0.5,
                  minWidth: 0,
                  textDecoration: "underline",
                  // MDButton variant="text" 預設 color="white"，hover 會把字改成 white.focus（白字白底=看不見）
                  // → 這裡明確覆蓋 hover / focus / active 的顏色
                  "&:hover, &:focus, &:focus:not(:hover), &:active": {
                    color: COLORS.main,
                    opacity: 0.75,
                    backgroundColor: "transparent",
                    textDecoration: "underline",
                  },
                }}
              >
                {L.Content12 || "寄送重設連結"}
              </MDButton>
            </Box>
            <MDButton
              onClick={handleSubmit}
              sx={{
                px: 3,
                py: 1,
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 700,
                background: "linear-gradient(90deg, #ffd166 0%, #f88e2b 100%)",
                color: COLORS.white,
                boxShadow: "0 4px 12px rgba(248, 142, 43, 0.3)",
                "&:hover": {
                  background: "linear-gradient(90deg, #ffd166 0%, #f88e2b 100%)",
                  boxShadow: "0 6px 16px rgba(248, 142, 43, 0.4)",
                },
              }}
            >
              {L.Content6 || "更新密碼"}
            </MDButton>
          </Box>
        </Card>
      </Box>
    </MDBox>
  );
}
